(function (globalScope, factory) {
  'use strict';

  var calculator = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = calculator;
  }

  if (globalScope && globalScope.document) {
    globalScope.HauberVibrationCalculator = calculator;
    calculator.boot(globalScope.document);
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var STANDARD_GRAVITY = 9.80665;
  var ROOT_SELECTOR = '[data-vibration-calculator]';
  var DEFAULT_TARGET_KEY = 'velocity_rms';
  var FREQUENCY_FIELD_KEYS = Object.freeze(['frequency_hz', 'frequency_rpm']);
  var VALUE_FIELD_KEYS = Object.freeze([
    'acceleration_rms_g',
    'acceleration_peak_g',
    'acceleration_rms_si',
    'acceleration_peak_si',
    'velocity_rms',
    'velocity_peak',
    'displacement_rms_mm',
    'displacement_peak_mm',
    'displacement_peak_peak_mm',
    'displacement_rms_um',
    'displacement_peak_um',
    'displacement_peak_peak_um',
  ]);
  var OUTPUT_KEYS = Object.freeze(FREQUENCY_FIELD_KEYS.concat(VALUE_FIELD_KEYS));
  var QUANTITY_UNITS = Object.freeze({
    acceleration: Object.freeze(['g', 'm_s2']),
    velocity: Object.freeze(['mm_s']),
    displacement: Object.freeze(['mm', 'um']),
  });
  var VALUE_FIELD_DEFINITIONS = Object.freeze({
    acceleration_rms_g: Object.freeze({quantity: 'acceleration', amplitude: 'rms', unit: 'g'}),
    acceleration_peak_g: Object.freeze({quantity: 'acceleration', amplitude: 'peak', unit: 'g'}),
    acceleration_rms_si: Object.freeze({quantity: 'acceleration', amplitude: 'rms', unit: 'm_s2'}),
    acceleration_peak_si: Object.freeze({quantity: 'acceleration', amplitude: 'peak', unit: 'm_s2'}),
    velocity_rms: Object.freeze({quantity: 'velocity', amplitude: 'rms', unit: 'mm_s'}),
    velocity_peak: Object.freeze({quantity: 'velocity', amplitude: 'peak', unit: 'mm_s'}),
    displacement_rms_mm: Object.freeze({quantity: 'displacement', amplitude: 'rms', unit: 'mm'}),
    displacement_peak_mm: Object.freeze({quantity: 'displacement', amplitude: 'peak', unit: 'mm'}),
    displacement_peak_peak_mm: Object.freeze({quantity: 'displacement', amplitude: 'peak_peak', unit: 'mm'}),
    displacement_rms_um: Object.freeze({quantity: 'displacement', amplitude: 'rms', unit: 'um'}),
    displacement_peak_um: Object.freeze({quantity: 'displacement', amplitude: 'peak', unit: 'um'}),
    displacement_peak_peak_um: Object.freeze({quantity: 'displacement', amplitude: 'peak_peak', unit: 'um'}),
  });

  function VibrationCalculatorError(code) {
    this.name = 'VibrationCalculatorError';
    this.code = code;
    this.message = code;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, VibrationCalculatorError);
    }
  }

  VibrationCalculatorError.prototype = Object.create(Error.prototype);
  VibrationCalculatorError.prototype.constructor = VibrationCalculatorError;

  function fail(code) {
    throw new VibrationCalculatorError(code);
  }

  function parseLocalizedDecimal(rawValue, errorCode) {
    var code = errorCode || 'value';

    if (typeof rawValue === 'number') {
      if (!Number.isFinite(rawValue)) fail(code);
      return rawValue;
    }

    if (typeof rawValue !== 'string') fail(code);

    var normalized = rawValue.trim();
    if (!/^[+-]?\d+(?:[.,]\d+)?$/.test(normalized)) fail(code);

    var parsed = Number(normalized.replace(',', '.'));
    if (!Number.isFinite(parsed)) fail(code);

    return parsed;
  }

  function parseOptionalDecimal(rawValue, errorCode) {
    var code = errorCode || 'value';

    if (typeof rawValue === 'string' && rawValue.trim() === '') return null;

    try {
      return parseLocalizedDecimal(rawValue, code);
    } catch (error) {
      if (error instanceof VibrationCalculatorError && error.code === code) return null;
      throw error;
    }
  }

  function convertVibration(input) {
    if (!input || typeof input !== 'object') fail('configuration');

    var quantity = input.quantity;
    var unit = input.unit;
    var amplitude = input.amplitude;
    var frequencyUnit = input.frequencyUnit || input.frequency_unit;
    var value = parseLocalizedDecimal(input.value, 'value');
    var frequency = parseLocalizedDecimal(input.frequency, 'frequency');

    if (value < 0 || Object.is(value, -0)) value = 0;

    if (!Object.prototype.hasOwnProperty.call(QUANTITY_UNITS, quantity)) fail('selection');
    if (QUANTITY_UNITS[quantity].indexOf(unit) === -1) fail('selection');
    if (amplitude !== 'rms' && amplitude !== 'peak' && amplitude !== 'peak_peak') fail('selection');
    if (amplitude === 'peak_peak' && quantity !== 'displacement') fail('selection');
    if (frequencyUnit !== 'hz' && frequencyUnit !== 'rpm') fail('selection');
    if (frequency <= 0) fail('frequency');

    var frequencyHz = frequencyUnit === 'rpm' ? frequency / 60 : frequency;
    if (!Number.isFinite(frequencyHz) || frequencyHz <= 0) fail('frequency');

    var angularFrequency = 2 * Math.PI * frequencyHz;
    var rmsInput = value;
    if (amplitude === 'peak') rmsInput = value / Math.SQRT2;
    else if (amplitude === 'peak_peak') rmsInput = value / (2 * Math.SQRT2);
    var accelerationRmsSi;
    var velocityRmsSi;
    var displacementRmsSi;

    if (quantity === 'acceleration') {
      accelerationRmsSi = unit === 'g' ? rmsInput * STANDARD_GRAVITY : rmsInput;
      velocityRmsSi = accelerationRmsSi / angularFrequency;
      displacementRmsSi = accelerationRmsSi / (angularFrequency * angularFrequency);
    } else if (quantity === 'velocity') {
      velocityRmsSi = rmsInput / 1000;
      accelerationRmsSi = angularFrequency * velocityRmsSi;
      displacementRmsSi = velocityRmsSi / angularFrequency;
    } else {
      displacementRmsSi = unit === 'um' ? rmsInput / 1000000 : rmsInput / 1000;
      velocityRmsSi = angularFrequency * displacementRmsSi;
      accelerationRmsSi = angularFrequency * velocityRmsSi;
    }

    var result = {
      frequency_hz: frequencyHz,
      frequency_rpm: frequencyHz * 60,
      acceleration_rms_g: accelerationRmsSi / STANDARD_GRAVITY,
      acceleration_rms_si: accelerationRmsSi,
      acceleration_peak_g: accelerationRmsSi * Math.SQRT2 / STANDARD_GRAVITY,
      acceleration_peak_si: accelerationRmsSi * Math.SQRT2,
      velocity_rms: velocityRmsSi * 1000,
      velocity_peak: velocityRmsSi * Math.SQRT2 * 1000,
      displacement_rms_mm: displacementRmsSi * 1000,
      displacement_peak_mm: displacementRmsSi * Math.SQRT2 * 1000,
      displacement_peak_peak_mm: displacementRmsSi * 2 * Math.SQRT2 * 1000,
      displacement_rms_um: displacementRmsSi * 1000000,
      displacement_peak_um: displacementRmsSi * Math.SQRT2 * 1000000,
      displacement_peak_peak_um: displacementRmsSi * 2 * Math.SQRT2 * 1000000,
      source: {
        quantity: quantity,
        unit: unit,
        amplitude: amplitude,
        value: value,
        frequency: frequency,
        frequency_unit: frequencyUnit,
      },
    };

    for (var index = 0; index < OUTPUT_KEYS.length; index += 1) {
      if (!Number.isFinite(result[OUTPUT_KEYS[index]])) fail('value');
    }

    return result;
  }

  function formatNumber(value, locale) {
    if (!Number.isFinite(value)) fail('configuration');

    var absoluteValue = Math.abs(value);
    var options = {
      maximumSignificantDigits: 5,
      useGrouping: false,
    };

    if (absoluteValue !== 0 && (absoluteValue < 0.000001 || absoluteValue >= 1000000000)) {
      options.notation = 'scientific';
    }

    return new Intl.NumberFormat(locale || undefined, options).format(value);
  }

  function replaceTemplate(template, replacements) {
    if (typeof template !== 'string' || template.length === 0) fail('configuration');

    function replaceMatch(match, key) {
      if (!Object.prototype.hasOwnProperty.call(replacements, key)) return match;
      return String(replacements[key]);
    }

    return template
      .replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, replaceMatch)
      .replace(/%\{\s*([a-z_]+)\s*\}/gi, replaceMatch);
  }

  function readConfig(root) {
    var configElement = root.querySelector('[data-vibration-config]');
    if (!configElement) fail('configuration');

    try {
      var config = JSON.parse(configElement.textContent);
      if (!config || typeof config !== 'object' || typeof config.basis_template !== 'string') {
        fail('configuration');
      }
      return config;
    } catch (error) {
      if (error instanceof VibrationCalculatorError) throw error;
      fail('configuration');
    }
  }

  function toArray(collection) {
    return Array.prototype.slice.call(collection || []);
  }

  function getValueFields(root) {
    return toArray(root.querySelectorAll('[data-vibration-value-field]'));
  }

  function getFrequencyFields(root) {
    return toArray(root.querySelectorAll('[data-vibration-frequency-field]'));
  }

  function getValueField(root, key) {
    return root.querySelector('[data-vibration-value-field="' + key + '"]');
  }

  function getState(root) {
    return root.__hauberVibrationCalculatorState;
  }

  function setBasis(root, text, isCalculated) {
    var basisElement = root.querySelector('[data-vibration-basis]');
    var basisPanel = root.querySelector('[data-vibration-basis-panel]');
    if (!basisElement) return;

    basisElement.textContent = text;
    if (isCalculated) {
      basisElement.setAttribute('data-vibration-calculated', 'true');
      if (basisPanel) basisPanel.setAttribute('data-vibration-calculated', 'true');
    } else {
      basisElement.removeAttribute('data-vibration-calculated');
      if (basisPanel) basisPanel.removeAttribute('data-vibration-calculated');
    }
  }

  function setStatus(root, text) {
    var statusElement = root.querySelector('[data-vibration-status]');
    if (statusElement) statusElement.textContent = text;
  }

  function markSource(root, selectedField) {
    var cards = root.querySelectorAll('[data-vibration-value-card]');

    for (var index = 0; index < cards.length; index += 1) {
      var field = cards[index].querySelector('[data-vibration-value-field]');
      var selected = field === selectedField;

      if (selected) cards[index].setAttribute('data-vibration-source', 'true');
      else cards[index].removeAttribute('data-vibration-source');
    }
  }

  function markFrequencySource(root, selectedField) {
    var cards = root.querySelectorAll('[data-vibration-frequency-card]');

    for (var index = 0; index < cards.length; index += 1) {
      var field = cards[index].querySelector('[data-vibration-frequency-field]');
      var selected = field === selectedField;

      if (selected) cards[index].setAttribute('data-vibration-frequency-source', 'true');
      else cards[index].removeAttribute('data-vibration-frequency-source');
    }
  }

  function dispatchTargetChange(root, field) {
    var view = root.ownerDocument && root.ownerDocument.defaultView;
    if (!view || typeof view.CustomEvent !== 'function') return;
    var state = getState(root);

    root.dispatchEvent(new view.CustomEvent('hauber:vibration-target-change', {
      bubbles: true,
      detail: {
        key: field ? field.getAttribute('data-vibration-value-field') : null,
        quantity: field ? field.getAttribute('data-quantity') : null,
        amplitude: field ? field.getAttribute('data-amplitude') : null,
        unit: field ? field.getAttribute('data-unit') : null,
        result: state.result,
      },
    }));
  }

  function dispatchResultChange(root, result) {
    var view = root.ownerDocument && root.ownerDocument.defaultView;
    if (!view || typeof view.CustomEvent !== 'function') return;

    root.dispatchEvent(new view.CustomEvent('hauber:vibration-result-change', {
      bubbles: true,
      detail: {result: result},
    }));
  }

  function selectTarget(root, field, config, announce) {
    var state = getState(root);
    var cards = root.querySelectorAll('[data-vibration-value-card]');

    for (var index = 0; index < cards.length; index += 1) {
      var cardField = cards[index].querySelector('[data-vibration-value-field]');
      var button = cards[index].querySelector('[data-vibration-target-button]');
      var badge = cards[index].querySelector('[data-vibration-target-badge]');
      var selected = cardField === field;

      if (selected) cards[index].setAttribute('data-vibration-target', 'true');
      else cards[index].removeAttribute('data-vibration-target');
      if (button) button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      if (badge) badge.hidden = !selected;
    }

    state.targetField = field;
    root.setAttribute('data-vibration-target-key', field.getAttribute('data-vibration-value-field'));
    markSource(root, state.sourceField);

    if (announce) {
      setStatus(root, replaceTemplate(config.target_status_template, {
        field: field.getAttribute('data-field-label'),
      }));
    }

    dispatchTargetChange(root, field);
  }

  function clearTarget(root, config, announce) {
    var state = getState(root);
    var clearedField = state.targetField;
    var cards = root.querySelectorAll('[data-vibration-value-card]');

    for (var index = 0; index < cards.length; index += 1) {
      cards[index].removeAttribute('data-vibration-target');
      var button = cards[index].querySelector('[data-vibration-target-button]');
      var badge = cards[index].querySelector('[data-vibration-target-badge]');
      if (button) button.setAttribute('aria-pressed', 'false');
      if (badge) badge.hidden = true;
    }

    state.targetField = null;
    root.removeAttribute('data-vibration-target-key');
    markSource(root, state.sourceField);

    if (announce && clearedField && config && typeof config.target_cleared_status_template === 'string') {
      setStatus(root, replaceTemplate(config.target_cleared_status_template, {
        field: clearedField.getAttribute('data-field-label'),
      }));
    }

    if (clearedField) dispatchTargetChange(root, null);
  }

  function toggleTarget(root, field, config) {
    if (field === getState(root).targetField) {
      clearTarget(root, config, true);
    } else {
      selectTarget(root, field, config, true);
    }
  }

  function clearValueFields(root, preservedField) {
    var fields = getValueFields(root);

    for (var index = 0; index < fields.length; index += 1) {
      if (fields[index] !== preservedField) fields[index].value = '';
      fields[index].removeAttribute('data-raw-value');
    }
  }

  function clearFrequencyFields(root, preservedField) {
    var fields = getFrequencyFields(root);

    for (var index = 0; index < fields.length; index += 1) {
      if (fields[index] !== preservedField) fields[index].value = '';
      fields[index].removeAttribute('data-raw-value');
    }
  }

  function renderFrequencyFields(root, config, state, frequencyHz) {
    var fields = getFrequencyFields(root);

    for (var index = 0; index < fields.length; index += 1) {
      var key = fields[index].getAttribute('data-vibration-frequency-field');
      var value = key === 'frequency_hz' ? frequencyHz : frequencyHz * 60;
      fields[index].setAttribute('data-raw-value', String(value));
      if (fields[index] !== state.frequencyField) {
        fields[index].value = formatNumber(value, config.locale);
      }
    }
  }

  function renderBasis(root, config, state, result) {
    setBasis(root, replaceTemplate(config.basis_template, {
      quantity: state.sourceField.getAttribute('data-quantity-label'),
      value: formatNumber(result.source.value, config.locale),
      unit: state.sourceField.getAttribute('data-unit-label'),
      amplitude: state.sourceField.getAttribute('data-amplitude-label'),
      frequency: formatNumber(result.source.frequency, config.locale),
      frequency_unit: state.frequencyField.getAttribute('data-frequency-unit-label'),
    }), true);
  }

  function renderResult(root, config, state, result) {
    var fields = getValueFields(root);

    for (var index = 0; index < fields.length; index += 1) {
      var key = fields[index].getAttribute('data-output-key');
      var value = result[key];
      fields[index].setAttribute('data-raw-value', String(value));
      if (fields[index] !== state.sourceField) {
        fields[index].value = formatNumber(value, config.locale);
      }
    }

    renderFrequencyFields(root, config, state, result.frequency_hz);
    renderBasis(root, config, state, result);
    state.result = result;
    dispatchResultChange(root, result);
    setStatus(root, replaceTemplate(config.status_template, {
      field: state.sourceField.getAttribute('data-field-label'),
    }));
  }

  function calculateAndRender(root, config) {
    var state = getState(root);

    if (!state.sourceField || state.sourceValue === null) {
      setBasis(root, config.value_needed, false);
      return null;
    }

    if (!state.frequencyField || state.frequencyValue === null) {
      setBasis(root, config.frequency_needed, false);
      return null;
    }

    var definition = VALUE_FIELD_DEFINITIONS[state.sourceField.getAttribute('data-vibration-value-field')];
    if (!definition) fail('configuration');

    try {
      var result = convertVibration({
        quantity: definition.quantity,
        unit: definition.unit,
        amplitude: definition.amplitude,
        value: state.sourceValue,
        frequency: state.frequencyValue,
        frequencyUnit: state.frequencyField.getAttribute('data-frequency-unit'),
      });
      renderResult(root, config, state, result);
      return result;
    } catch (error) {
      state.result = null;
      clearValueFields(root, state.sourceField);
      setBasis(root, config.empty_basis, false);
      setStatus(root, '');
      dispatchResultChange(root, null);
      return null;
    }
  }

  function normalizeFieldValue(field, code) {
    var value = parseOptionalDecimal(field.value, code);

    if (value !== null && (value < 0 || Object.is(value, -0))) {
      field.value = '0';
      return 0;
    }

    return value;
  }

  function handleValueInput(root, config, field) {
    var state = getState(root);
    state.result = null;
    setBasis(root, config.empty_basis, false);
    setStatus(root, '');

    var value = normalizeFieldValue(field, 'value');
    if (value === null) {
      state.sourceField = null;
      state.sourceValue = null;
      state.sourceRaw = field.value;
      markSource(root, null);
      clearValueFields(root, field);
      setBasis(
        root,
        state.frequencyField && state.frequencyValue !== null ? config.value_needed : config.empty_basis,
        false
      );
      dispatchResultChange(root, null);
      return;
    }

    state.sourceField = field;
    state.sourceValue = value;
    state.sourceRaw = field.value;
    markSource(root, field);

    if (!state.frequencyField || state.frequencyValue === null) {
      clearValueFields(root, field);
      setBasis(root, config.frequency_needed, false);
      dispatchResultChange(root, null);
      return;
    }

    calculateAndRender(root, config);
  }

  function handleFrequencyInput(root, config, field) {
    var state = getState(root);
    state.result = null;
    setBasis(root, config.empty_basis, false);
    setStatus(root, '');

    var value = normalizeFieldValue(field, 'frequency');
    var frequencyHz = field.getAttribute('data-frequency-unit') === 'rpm' ? value / 60 : value;
    var validFrequency = value !== null
      && value > 0
      && Number.isFinite(frequencyHz)
      && Number.isFinite(frequencyHz * 60);

    if (!validFrequency) {
      state.frequencyField = null;
      state.frequencyValue = null;
      state.frequencyRaw = field.value;
      markFrequencySource(root, null);
      clearFrequencyFields(root, field);
      clearValueFields(root, state.sourceField);
      setBasis(
        root,
        state.sourceField && state.sourceValue !== null ? config.frequency_needed : config.empty_basis,
        false
      );
      dispatchResultChange(root, null);
      return;
    }

    state.frequencyField = field;
    state.frequencyValue = value;
    state.frequencyRaw = field.value;
    markFrequencySource(root, field);
    renderFrequencyFields(root, config, state, frequencyHz);

    if (!state.sourceField || state.sourceValue === null) {
      setBasis(root, config.value_needed, false);
      dispatchResultChange(root, null);
      return;
    }

    calculateAndRender(root, config);
  }

  function resetRoot(root, config) {
    var state = getState(root);
    var fields = getValueFields(root).concat(getFrequencyFields(root));

    for (var index = 0; index < fields.length; index += 1) {
      fields[index].value = '';
      fields[index].removeAttribute('data-raw-value');
    }

    state.sourceField = null;
    state.sourceValue = null;
    state.sourceRaw = '';
    state.frequencyField = null;
    state.frequencyValue = null;
    state.frequencyRaw = '';
    state.result = null;
    markSource(root, null);
    markFrequencySource(root, null);
    var defaultTarget = getValueField(root, DEFAULT_TARGET_KEY);
    if (defaultTarget) selectTarget(root, defaultTarget, config, false);
    else clearTarget(root, config, false);
    setBasis(root, config.empty_basis, false);
    setStatus(root, '');
    dispatchResultChange(root, null);
  }

  function markupIsComplete(root) {
    if (getValueFields(root).length !== VALUE_FIELD_KEYS.length) return false;
    if (getFrequencyFields(root).length !== FREQUENCY_FIELD_KEYS.length) return false;

    for (var index = 0; index < VALUE_FIELD_KEYS.length; index += 1) {
      var field = getValueField(root, VALUE_FIELD_KEYS[index]);
      var definition = VALUE_FIELD_DEFINITIONS[VALUE_FIELD_KEYS[index]];
      if (!field) return false;
      if (field.getAttribute('data-quantity') !== definition.quantity) return false;
      if (field.getAttribute('data-amplitude') !== definition.amplitude) return false;
      if (field.getAttribute('data-unit') !== definition.unit) return false;
    }

    return true;
  }

  function initRoot(root) {
    if (!root || root.__hauberVibrationCalculatorInitialized) return false;

    var config;
    try {
      config = readConfig(root);
    } catch (error) {
      var unavailableForm = root.querySelector('[data-vibration-form]');
      if (unavailableForm) unavailableForm.hidden = true;
      return false;
    }

    var form = root.querySelector('[data-vibration-form]');
    if (!form || !markupIsComplete(root)) {
      if (form) form.hidden = true;
      return false;
    }

    root.__hauberVibrationCalculatorInitialized = true;
    root.__hauberVibrationCalculatorState = {
      sourceField: null,
      sourceValue: null,
      sourceRaw: '',
      frequencyField: null,
      frequencyValue: null,
      frequencyRaw: '',
      targetField: null,
      result: null,
    };
    form.noValidate = true;
    setBasis(root, config.empty_basis, false);

    form.addEventListener('input', function (event) {
      if (event.target.hasAttribute('data-vibration-value-field')) {
        handleValueInput(root, config, event.target);
      } else if (event.target.hasAttribute('data-vibration-frequency-field')) {
        handleFrequencyInput(root, config, event.target);
      }
    });

    form.addEventListener('click', function (event) {
      var button = event.target.closest('[data-vibration-target-button]');
      if (!button || !form.contains(button)) return;
      var field = getValueField(root, button.getAttribute('data-vibration-target-button'));
      if (field) toggleTarget(root, field, config);
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
    });

    form.addEventListener('reset', function (event) {
      event.preventDefault();
      resetRoot(root, config);
    });

    var defaultTarget = getValueField(root, DEFAULT_TARGET_KEY);
    if (defaultTarget) selectTarget(root, defaultTarget, config, false);

    return true;
  }

  function init(scope) {
    if (!scope) return 0;

    var roots = [];
    if (scope.matches && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    if (scope.querySelectorAll) {
      var descendants = scope.querySelectorAll(ROOT_SELECTOR);
      for (var index = 0; index < descendants.length; index += 1) roots.push(descendants[index]);
    }

    var initializedCount = 0;
    for (var rootIndex = 0; rootIndex < roots.length; rootIndex += 1) {
      if (initRoot(roots[rootIndex])) initializedCount += 1;
    }

    return initializedCount;
  }

  function boot(documentReference) {
    if (!documentReference) return;

    if (documentReference.readyState === 'loading') {
      if (!documentReference.__hauberVibrationCalculatorReadyListener) {
        documentReference.__hauberVibrationCalculatorReadyListener = true;
        documentReference.addEventListener('DOMContentLoaded', function () {
          init(documentReference);
        });
      }
    } else {
      init(documentReference);
    }

    if (!documentReference.__hauberVibrationCalculatorSectionListener) {
      documentReference.__hauberVibrationCalculatorSectionListener = true;
      documentReference.addEventListener('shopify:section:load', function (event) {
        init(event.target);
      });
    }
  }

  return Object.freeze({
    DEFAULT_TARGET_KEY: DEFAULT_TARGET_KEY,
    FREQUENCY_FIELD_KEYS: FREQUENCY_FIELD_KEYS,
    OUTPUT_KEYS: OUTPUT_KEYS,
    QUANTITY_UNITS: QUANTITY_UNITS,
    STANDARD_GRAVITY: STANDARD_GRAVITY,
    VALUE_FIELD_DEFINITIONS: VALUE_FIELD_DEFINITIONS,
    VALUE_FIELD_KEYS: VALUE_FIELD_KEYS,
    VibrationCalculatorError: VibrationCalculatorError,
    boot: boot,
    convertVibration: convertVibration,
    formatNumber: formatNumber,
    init: init,
    parseLocalizedDecimal: parseLocalizedDecimal,
    parseOptionalDecimal: parseOptionalDecimal,
    replaceTemplate: replaceTemplate,
  });
});
